/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import React from "react";
import { css } from "styled-components";

import { connect } from "react-redux";

const enum fields {
    title = "title",
    headingLevel = "headingLevel",
    link = "link",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
}

interface TitleProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.headingLevel]: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        [fields.link]: LinkFieldValue;
        [fields.linkText]: string;
        [fields.openInNewTab]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, titleProps: TitleProps) => {
    let linkData;

    if (titleProps.fields.link && titleProps.fields.link.value) {
        linkData = getLink(state, titleProps.fields.link);
    }

    return { url: linkData?.url };
};

type Props = TitleProps & ReturnType<typeof mapStateToProps>;

const wrapperCss = css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 25px;
`;

const titleCss = css`
    margin-bottom: 0;
`;

const linkTypographyProps = { size: "18px" };

const TitleWidget: React.FunctionComponent<Props> = ({ fields, url }) => {
    let theLink;

    if (url) {
        theLink = (
            <Link
                href={url}
                target={fields.openInNewTab ? "_blank" : undefined}
                color="primary.main"
                typographyProps={linkTypographyProps}
            >
                {fields.linkText}
            </Link>
        );
    }

    return (
        <StyledWrapper css={wrapperCss}>
            <Typography variant={fields.headingLevel || "h2"} css={titleCss}>
                {fields.title}
            </Typography>
            {theLink}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TitleWidget),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.headingLevel,
                displayName: "Heading Level",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "H1", value: "h1" },
                    { displayName: "H2", value: "h2" },
                    { displayName: "H3", value: "h3" },
                    { displayName: "H4", value: "h4" },
                    { displayName: "H5", value: "h5" },
                    { displayName: "H6", value: "h6" },
                ],
                isRequired: true,
                defaultValue: "h2",
                fieldType: "General",
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Page" },
                fieldType: "General",
            },
            {
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate: (value: string | null, item: HasFields) => {
                    if (item.fields.link && item.fields.link.value && !value) {
                        return "A value must be provided.";
                    }
                    return null;
                },
            },
            {
                name: fields.openInNewTab,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
